import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useProjects } from "@/hooks/use-projects";
import type { SearchScope } from "@/components/layout/search-overlay-context";
import type { Project, Video } from "@/lib/api-types";

export type SearchResults = {
  projects: Project[];
  videos: Video[];
  isLoading: boolean;
};

/**
 * Global search across projects + files, filtered by scope.
 *
 *   workspace  — projects (client-side filter) + all matching videos
 *   project    — no projects; videos limited to that project
 *   folder     — no projects; videos limited to that folder
 *
 * Videos are always fetched server-side via GET /editor/videos?q= (+ optional
 * projectId / folderId). Projects are filtered client-side against the
 * cached useProjects() list — no extra round-trip.
 */
export function useSearch(
  query: string,
  options?: { enabled?: boolean; scope?: SearchScope },
): SearchResults {
  const enabled = options?.enabled ?? true;
  const scope: SearchScope = options?.scope ?? { kind: "workspace" };
  const q = query.trim();
  const active = enabled && q.length > 0;

  // Projects list is only useful for workspace scope; still fetch conditionally
  // so the useProjects cache stays warm.
  const projectsQuery = useProjects(enabled && scope.kind === "workspace");

  // Build the videos query URL — includes q + scope-specific filters.
  const videosUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("q", q);
    if (scope.kind === "project") params.set("projectId", scope.projectId);
    if (scope.kind === "folder") params.set("folderId", scope.folderId);
    return `/editor/videos?${params.toString()}`;
  }, [q, scope]);

  const videosQuery = useQuery<Video[]>({
    // Include scope in queryKey so switching from workspace → project doesn't
    // reuse a stale cache.
    queryKey: ["search-videos", q, JSON.stringify(scope)],
    queryFn: () => api.get<Video[]>(videosUrl),
    enabled: active,
    staleTime: 15_000,
  });

  const filteredProjects = useMemo(() => {
    if (!active || scope.kind !== "workspace") return [];
    const needle = q.toLowerCase();
    return (projectsQuery.data ?? [])
      .filter((p) => p.name.toLowerCase().includes(needle))
      .slice(0, 6);
  }, [projectsQuery.data, active, q, scope]);

  const filteredVideos = useMemo(() => {
    if (!active) return [];
    // Server already narrowed by q and scope; just cap for the dropdown case.
    return (videosQuery.data ?? []).slice(0, 8);
  }, [videosQuery.data, active]);

  return {
    projects: filteredProjects,
    videos: filteredVideos,
    isLoading:
      active &&
      ((scope.kind === "workspace" && projectsQuery.isLoading) || videosQuery.isLoading),
  };
}
